/**
 * Chart components for the Analyse dashboard.
 * Contains KPI, Bar, Line, Pie, Summary indicators and the IndicatorCard wrapper.
 */

import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  BarChart3,
  LineChart as LineChartIcon,
  PieChart as PieChartIcon,
  Gauge,
  Grid3X3,
  TrendingUp,
  TrendingDown,
  Edit,
  Trash2,
  MoreVertical,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';
import type { DashboardIndicator } from '../../lib/api/barratt';

// Chart colors palette
export const CHART_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#06b6d4', '#f97316', '#84cc16', '#ec4899', '#6366f1',
];

// Chart type definitions for the indicator creator
export const CHART_TYPES = [
  { value: 'bar', label: 'Bar Chart', icon: <BarChart3 className="h-5 w-5" />, description: 'Compare values across categories' },
  { value: 'line', label: 'Line Chart', icon: <LineChartIcon className="h-5 w-5" />, description: 'Show trends over time' },
  { value: 'pie', label: 'Pie Chart', icon: <PieChartIcon className="h-5 w-5" />, description: 'Show proportions of a whole' },
  { value: 'donut', label: 'Donut Chart', icon: <PieChartIcon className="h-5 w-5" />, description: 'Pie chart with center cutout' },
  { value: 'kpi', label: 'KPI Indicator', icon: <Gauge className="h-5 w-5" />, description: 'Single value with target' },
  { value: 'summary', label: 'Summary Grid', icon: <Grid3X3 className="h-5 w-5" />, description: 'Multiple metrics overview' },
];

// Chart type icons
export const CHART_TYPE_ICONS: Record<string, React.ReactNode> = {
  bar: <BarChart3 className="h-4 w-4" />,
  line: <LineChartIcon className="h-4 w-4" />,
  pie: <PieChartIcon className="h-4 w-4" />,
  donut: <PieChartIcon className="h-4 w-4" />,
  kpi: <Gauge className="h-4 w-4" />,
  summary: <Grid3X3 className="h-4 w-4" />,
  grid: <Grid3X3 className="h-4 w-4" />,
  heatmap: <Grid3X3 className="h-4 w-4" />,
  waterfall: <BarChart3 className="h-4 w-4" />,
};

// Mock data generator for demo purposes
const generateMockData = (chartType: string) => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
  const categories = ['Category A', 'Category B', 'Category C', 'Category D', 'Category E'];

  switch (chartType) {
    case 'bar':
    case 'line':
    case 'waterfall':
      return months.map((month) => ({
        name: month,
        value: Math.floor(Math.random() * 1000) + 100,
        target: Math.floor(Math.random() * 800) + 200,
      }));
    case 'pie':
    case 'donut':
      return categories.map((cat, index) => ({
        name: cat,
        value: Math.floor(Math.random() * 500) + 100,
        color: CHART_COLORS[index % CHART_COLORS.length],
      }));
    case 'kpi':
      return {
        value: Math.floor(Math.random() * 100),
        target: 80,
        trend: Math.random() > 0.5 ? 'up' : 'down',
        change: (Math.random() * 20 - 10).toFixed(1),
      };
    default:
      return months.map((month) => ({
        name: month,
        value: Math.floor(Math.random() * 1000) + 100,
      }));
  }
};

// KPI Indicator Component
function KPIIndicator({ indicator: _indicator }: { indicator: DashboardIndicator }) {
  const data = useMemo(() => generateMockData('kpi') as {
    value: number; target: number; trend: string; change: string;
  }, []);

  const isOnTarget = data.value >= data.target;
  const trendUp = data.trend === 'up';

  return (
    <div className="flex flex-col items-center justify-center h-full p-4">
      <div className="text-4xl font-bold mb-2">{data.value}%</div>
      <div className="flex items-center gap-2 text-sm">
        {trendUp ? (
          <TrendingUp className={`h-4 w-4 ${isOnTarget ? 'text-green-500' : 'text-red-500'}`} />
        ) : (
          <TrendingDown className={`h-4 w-4 ${isOnTarget ? 'text-green-500' : 'text-red-500'}`} />
        )}
        <span className={trendUp ? 'text-green-600' : 'text-red-600'}>
          {trendUp ? '+' : ''}{data.change}%
        </span>
      </div>
      <div className="text-sm text-muted-foreground mt-2">Target: {data.target}%</div>
      <Badge className={`mt-2 ${isOnTarget ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
        {isOnTarget ? 'On Target' : 'Below Target'}
      </Badge>
    </div>
  );
}

// Bar Chart Component
function BarChartIndicator({ indicator: _indicator }: { indicator: DashboardIndicator }) {
  const data = useMemo(() => generateMockData('bar') as Array<{ name: string; value: number; target: number }>, []);

  return (
    <ResponsiveContainer width="100%" height={250}>
      <BarChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" fontSize={12} />
        <YAxis fontSize={12} />
        <Tooltip />
        <Legend />
        <Bar dataKey="value" fill={CHART_COLORS[0]} name="Actual" radius={[4, 4, 0, 0]} />
        <Bar dataKey="target" fill={CHART_COLORS[1]} name="Target" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

// Line Chart Component
function LineChartIndicator({ indicator: _indicator }: { indicator: DashboardIndicator }) {
  const data = useMemo(() => generateMockData('line') as Array<{ name: string; value: number; target: number }>, []);

  return (
    <ResponsiveContainer width="100%" height={250}>
      <AreaChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
        <defs>
          <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={CHART_COLORS[0]} stopOpacity={0.3} />
            <stop offset="95%" stopColor={CHART_COLORS[0]} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" fontSize={12} />
        <YAxis fontSize={12} />
        <Tooltip />
        <Legend />
        <Area type="monotone" dataKey="value" stroke={CHART_COLORS[0]} fill="url(#colorValue)" name="Value" />
        <Line type="monotone" dataKey="target" stroke={CHART_COLORS[1]} strokeDasharray="5 5" name="Target" dot={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

// Pie Chart Component
function PieChartIndicator({ indicator: _indicator, isDoughnut = false }: { indicator: DashboardIndicator; isDoughnut?: boolean }) {
  const data = useMemo(() => generateMockData('pie') as Array<{ name: string; value: number; color: string }>, []);

  return (
    <ResponsiveContainer width="100%" height={250}>
      <PieChart>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          innerRadius={isDoughnut ? 60 : 0}
          outerRadius={80}
          paddingAngle={2}
          dataKey="value"
          label={(props) => `${props['name']} (${((props['percent'] as number) * 100).toFixed(0)}%)`}
          labelLine={false}
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.color} />
          ))}
        </Pie>
        <Tooltip />
      </PieChart>
    </ResponsiveContainer>
  );
}

// Summary/Grid Indicator Component
function SummaryIndicator({ indicator: _indicator }: { indicator: DashboardIndicator }) {
  const metrics = [
    { label: 'Total Orders', value: '1,234', change: '+12%' },
    { label: 'On Time', value: '89%', change: '+3%' },
    { label: 'Quality Score', value: '4.5', change: '-0.2' },
    { label: 'Avg Lead Time', value: '5.2 days', change: '-1.1' },
  ];

  return (
    <div className="grid grid-cols-2 gap-4 p-2">
      {metrics.map((metric, index) => (
        <div key={index} className="text-center p-3 bg-muted/50 rounded-lg">
          <div className="text-lg font-bold">{metric.value}</div>
          <div className="text-xs text-muted-foreground">{metric.label}</div>
          <div className={`text-xs ${metric.change.startsWith('+') ? 'text-green-600' : metric.change.startsWith('-') ? 'text-red-600' : 'text-gray-600'}`}>
            {metric.change}
          </div>
        </div>
      ))}
    </div>
  );
}

// Indicator Card Component
export function IndicatorCard({
  indicator,
  onEdit,
  onDelete
}: {
  indicator: DashboardIndicator;
  onEdit?: (indicator: DashboardIndicator) => void;
  onDelete?: (indicator: DashboardIndicator) => void;
}) {
  const renderChart = () => {
    switch (indicator.chart_type) {
      case 'kpi': return <KPIIndicator indicator={indicator} />;
      case 'bar': case 'waterfall': return <BarChartIndicator indicator={indicator} />;
      case 'line': return <LineChartIndicator indicator={indicator} />;
      case 'pie': return <PieChartIndicator indicator={indicator} />;
      case 'donut': return <PieChartIndicator indicator={indicator} isDoughnut />;
      case 'summary': case 'grid': return <SummaryIndicator indicator={indicator} />;
      case 'heatmap': return <div className="flex items-center justify-center h-[250px] text-muted-foreground">Heatmap visualization coming soon</div>;
      default: return <BarChartIndicator indicator={indicator} />;
    }
  };

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {CHART_TYPE_ICONS[indicator.chart_type] || <BarChart3 className="h-4 w-4" />}
            <CardTitle className="text-sm font-medium">{indicator.name}</CardTitle>
          </div>
          <div className="flex items-center gap-1">
            <Badge variant="outline" className="text-xs">
              {indicator.chart_type_display || indicator.chart_type}
            </Badge>
            {(onEdit || onDelete) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-6 w-6"><MoreVertical className="h-3 w-3" /></Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {onEdit && (<DropdownMenuItem onClick={() => onEdit(indicator)}><Edit className="h-4 w-4 mr-2" />Edit</DropdownMenuItem>)}
                  {onDelete && (<><DropdownMenuSeparator /><DropdownMenuItem className="text-destructive" onClick={() => onDelete(indicator)}><Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem></>)}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
        {indicator.hint && (<CardDescription className="text-xs">{indicator.hint}</CardDescription>)}
      </CardHeader>
      <CardContent className="pt-0">{renderChart()}</CardContent>
    </Card>
  );
}
